export interface ResolvableAccess {
  _id: { toString(): string } | string;
  tenant?: { permission?: { role?: string | null } | null } | null;
}

export interface ResolvableMe<T extends ResolvableAccess> {
  access?: T[] | null;
  schoolOfRecord?: { accessId?: { toString(): string } | string | null } | null;
}

const LEARNER_ROLE = 'Learner';
const ALUM_ROLE = 'Alum';

export const isLearnerRole = (role?: string | null): boolean => role === LEARNER_ROLE || role === ALUM_ROLE;

export const hasOnlyLearnerAccess = <T extends ResolvableAccess>(access: T[]): boolean =>
  access.length > 0 && access.every((a) => isLearnerRole(a?.tenant?.permission?.role));

const createdAt = (access: ResolvableAccess): number => parseInt(String(access._id).slice(0, 8), 16) || 0;

const rolePriority = (access: ResolvableAccess): number => (access.tenant?.permission?.role === LEARNER_ROLE ? 1 : 0);

export const resolveDefaultAccess = <T extends ResolvableAccess>(me: ResolvableMe<T>): T | undefined => {
  const access = me?.access ?? [];

  if (!access.length) return undefined;

  if (!hasOnlyLearnerAccess(access)) return access[0];

  const schoolOfRecordId = me.schoolOfRecord?.accessId ? String(me.schoolOfRecord.accessId) : undefined;
  const schoolOfRecordAccess = schoolOfRecordId ? access.find((a) => String(a._id) === schoolOfRecordId) : undefined;

  if (schoolOfRecordAccess) return schoolOfRecordAccess;

  return [...access].sort((a, b) => rolePriority(b) - rolePriority(a) || createdAt(b) - createdAt(a))[0];
};
